import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  TAppLanguageISOCountryCode,
  appLanguageManager,
  fallbackLanguage,
  getLanguageFromStorage,
  getUserLanguage
} from '@aqx-core/common/i18n';
import { ELanguageQueryString, mapLangQueryStringToTLang } from '@aqx-core/common/i18n/languages';
import { isLanguageSupported } from '@aqx-core/common/i18n/utils';
import {
  useAccountDetails,
  useAuthenticationAuthenticated,
  useAuthenticationLoading
} from '@aqx-core/common/services/auth';
import { authenticationViewController } from '@aqx-core/common/services/auth/controller';
import { useCompanyLangInitialised, useCompanyLoading, useCompanyProfile } from '@aqx-core/common/services/company';
import { EUISnackBarMessageType, snackbarController } from '@aqx-core/common/services/snackbar';

interface IUseUpdateLanguageFromQueryString {
  languageQueryString?: ELanguageQueryString;
}

export const useUpdateLanguageFromQueryString = ({ languageQueryString }: IUseUpdateLanguageFromQueryString) => {
  const authenticated = useAuthenticationAuthenticated();
  const accountLoading = useAuthenticationLoading();
  const { language: accountLanguage } = useAccountDetails() || {};
  const company = useCompanyProfile();
  const companyLoading = useCompanyLoading();
  const i18nLanguage = getUserLanguage() as TAppLanguageISOCountryCode;
  const companyLangInitialised = useCompanyLangInitialised();

  const { defaultLanguage = fallbackLanguage, supportedLanguages = null } = company || {};

  const langQueryStringRef = useRef<ELanguageQueryString | undefined>(languageQueryString);
  const [isLangSynced, setIsLangSynced] = useState<boolean>(false);

  const resolvedLanguage = useMemo(() => {
    let resultLang;

    //* Priority 1: Query string
    if (langQueryStringRef.current) {
      const mapped = mapLangQueryStringToTLang[langQueryStringRef.current];
      if (mapped && isLanguageSupported(mapped, supportedLanguages)) {
        resultLang = mapped;
      }
    }

    //* Priority 2: Account language (only if authenticated and has account language)
    if (!resultLang && authenticated && accountLanguage && isLanguageSupported(accountLanguage, supportedLanguages)) {
      resultLang = accountLanguage;
    }

    //* Priority 3: Stored language from storage
    if (!resultLang) {
      const storedLang = getLanguageFromStorage();
      if (storedLang && isLanguageSupported(storedLang, supportedLanguages)) {
        resultLang = storedLang;
      }
    }

    //* Priority 4: Default language
    if (!resultLang) {
      resultLang = defaultLanguage;
    }

    return resultLang;
  }, [defaultLanguage, authenticated, supportedLanguages, accountLanguage]);

  const handleChangeLanguage = useCallback(async () => {
    const shouldUpdateLanguage = resolvedLanguage !== i18nLanguage;
    if (shouldUpdateLanguage) {
      await appLanguageManager.changeLanguage({
        language: resolvedLanguage,
        updateLanguageCallback: async () => {
          if (authenticated) {
            await authenticationViewController.updateAccountLanguage((error) => {
              snackbarController.showAlert({
                message: error,
                messageType: EUISnackBarMessageType.ERROR
              });
            });
          }
        }
      });
    } else {
      // Language matches but ensure cookie is set
      const storedLang = getLanguageFromStorage();
      if (!storedLang) {
        appLanguageManager.setLanguagePreference(resolvedLanguage);
      }
    }

    if (langQueryStringRef.current) {
      langQueryStringRef.current = undefined;
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [authenticated, resolvedLanguage, i18nLanguage]);

  useEffect(() => {
    /**
     * WT-9721: Prevent handleChangeLanguage logic from evaluating before company data has been fully loaded
     * because handleChangeLanguage requires defaultLanguage and supportedLanguages fields
     */
    if (accountLoading || isLangSynced || companyLoading || !company || !companyLangInitialised) {
      return;
    }
    /**
     * WT-8792: Added setIsLangInitialised state to track if handleChangeLanguage has been run
     * And disable if it has been run once
     *
     * i.e. if the language has been changed once alr, make sure that this is not run again if
     * user changes defaultLanguage/supportedLanguages (esp. if they change tab)
     */
    handleChangeLanguage().then(() => setIsLangSynced(true));
  }, [handleChangeLanguage, accountLoading, companyLoading, company, isLangSynced, companyLangInitialised]);
};

export const useUpdateLanguageOnLoadMobile = () => {
  const company = useCompanyProfile();
  const loading = useCompanyLoading();
  const langInitLoading = useCompanyLangInitialised();
  const accountLoading = useAuthenticationLoading();
  const userLanguage = getUserLanguage();
  const { defaultLanguage, supportedLanguages } = company || {};
  const authenticated = useAuthenticationAuthenticated();
  const [isLangInitialised, setIsLangInitialised] = useState<boolean>(false);

  const handleChangeLanguage = useCallback(async () => {
    const selectedLanguage = defaultLanguage ?? fallbackLanguage;
    /**
     * WT-9721: Check if language has been stored in cookies (i.e. newly registered user)
     * If not, change to default language and store in cookies to prevent this from evaluating again when user changes language
     */
    const storedLanguage = getLanguageFromStorage();
    const isLangNotStored = !storedLanguage && selectedLanguage !== userLanguage;
    const hasUnsupportedLanguage =
      supportedLanguages && userLanguage && !supportedLanguages.includes(userLanguage as TAppLanguageISOCountryCode);
    const shouldUpdateLanguage = isLangNotStored || hasUnsupportedLanguage;

    if (shouldUpdateLanguage) {
      await appLanguageManager.changeLanguage({
        language: selectedLanguage,
        updateLanguageCallback: async () => {
          if (authenticated) {
            await authenticationViewController.updateAccountLanguage((error) => {
              snackbarController.showAlert({
                message: error,
                messageType: EUISnackBarMessageType.ERROR
              });
            });
          }
        }
      });
    }
  }, [authenticated, defaultLanguage, userLanguage, supportedLanguages]);

  useEffect(() => {
    /**
     * WT-9721: Prevent handleChangeLanguage logic from evaluating before company data has been fully loaded
     * because handleChangeLanguage requires defaultLanguage and supportedLanguages fields
     */
    if (accountLoading || isLangInitialised || loading || !company || langInitLoading) {
      return;
    }
    /**
     * WT-8792: Added setIsLangInitialised state to track if handleChangeLanguage has been run
     * And disable if it has been run once
     *
     * i.e. if the language has been changed once alr, make sure that this is not run again if
     * user changes defaultLanguage/supportedLanguages (esp. if they change tab)
     */
    handleChangeLanguage().then(() => setIsLangInitialised(true));
  }, [handleChangeLanguage, accountLoading, loading, company, isLangInitialised, langInitLoading]);
};
